import { Button, Clickable, H2, Hidden, Image, SpacerWrapper, Strong, TypeWrapper } from '@ahmdigital/ui';
import { ComponentType, ReactNode, useEffect } from 'react';
import { compose } from 'redux';
import { connect } from 'react-redux';
import { isEmpty, isNull, join, map } from 'lodash/fp';
import classNames from 'classnames';

import { CustomerState } from '../../../types/state/customer';
import { ProductType } from '../../../types/product';
import { RootState } from '../../../reducers';
import { StyledNeedsCTA } from './styles';
import buildFinancialProductItemListSchema from '../../structured-data/utils/build-financial-product-item-list-schema';
import buildFinancialProductSchema from '../../structured-data/utils/build-financial-product-schema';
import buildFinancialProductSourceFromRuntime from '../../structured-data/utils/build-financial-product-source-from-runtime';
import constants from '../../../ahm-constants';
import getCustomerHasChanged from '../../../utils/customer-has-changed';
import getFutureDate from '../../../utils/get-future-date';
import NeedsProductDataLoader from '../../needs-product-data-loader';
import PremiumReviewToggle from '../../premium-review-toggle-wrapper';
import PriceDisclaimer from '../../price-disclaimer';
import PricingDrawer from '../../pricing-drawer';
import productListingCommon from '../../../pages/product-listing/product-listing-common';
import ProductService from '../../../models/product';
import ProductTiles from './components/product-tiles';
import selectCustomer from '../../../selectors/select-customer';
import selectNeedsProductType from '../../../selectors/select-needs-product-type';
import selectNeedsType from '../../../selectors/select-needs-type';
import StructuredData from '../../structured-data';
import styles from './styles.module.sass';
import WrappedSection from '../wrapped-section';

type ProductsSectionProps = {
  actions?: unknown;
  compareButtonText?: string | null;
  compareButtonToLink?: string | null;
  customer: CustomerState;
  data?: { subtitle?: string | ReactNode; title: string } | null;
  image?: { alt: string; src: string } | null;
  isLoadingPrice?: boolean;
  prices?: unknown;
  productIds?: string[];
  products: ProductType[] | null;
  stateProducts?: Record<string, ProductType>;
  title?: string;
  useFutureEffectiveDate?: boolean;
  withBorder?: boolean;
  withDrawer?: boolean;
  withHorizontalScroll?: boolean;
  withNeedsLink?: boolean;
  withVerticalSpacing?: boolean;
};

const ProductsSection = ({
  actions,
  compareButtonText,
  compareButtonToLink,
  customer,
  data = null,
  image = null,
  isLoadingPrice = false,
  prices,
  products,
  productIds: productIdsToLoad,
  stateProducts,
  title: titleFromSanity,
  useFutureEffectiveDate,
  withBorder = false,
  withDrawer = true,
  withHorizontalScroll = false,
  withNeedsLink = false,
  withVerticalSpacing = true,
}: ProductsSectionProps) => {
  const productIds = productIdsToLoad || map('id', products);
  const resolvedProducts = ProductService.getProducts(stateProducts, productIds);
  const productsToLoad = products || resolvedProducts;
  const productIdsHaveChanged = join('|', productIds);

  const customerHasChanged = getCustomerHasChanged(customer, useFutureEffectiveDate);
  const subtitle = data?.subtitle;
  const title = titleFromSanity || data?.title;

  const hasHeaderDetails = !isNull(image) || !isEmpty(title) || !isEmpty(subtitle);

  useEffect(() => {
    // This component is only used on pages where only 750 excess is sold
    // @ts-expect-error - Automatic, Please fix when editing this file
    actions.customer.changeHospitalExcess('750');
    // @ts-expect-error - Automatic, Please fix when editing this file
    actions.price.load(productIds, customer, { date: getFutureDate(useFutureEffectiveDate) });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customerHasChanged, productIdsHaveChanged, useFutureEffectiveDate]);

  const financialProductSchemas = productsToLoad.map((product) =>
    buildFinancialProductSchema(
      buildFinancialProductSourceFromRuntime({
        product: product as ProductType,
      }),
    ),
  );

  const financialProductItemListSchema = buildFinancialProductItemListSchema(financialProductSchemas);

  return (
    <>
      <StructuredData data={financialProductItemListSchema} />
      <WrappedSection withBorder={withBorder} spacerSize={withVerticalSpacing ? 3 : 12}>
        <TypeWrapper variant="center">
          {/* @ts-expect-error - Automatic, Please fix when editing this file */}
          <div className={classNames({ [styles.verticalSpacingWrapper]: withVerticalSpacing })}>
            <div className={classNames(styles.horizontalSpacingWrapper, styles.spacingWrapper)}>
              {hasHeaderDetails && (
                <SpacerWrapper variant="marginBottomSpacer6">
                  {image && <Image {...image} className={styles.image} />}
                  <H2>{title}</H2>
                  {subtitle && (
                    <Hidden xs sm>
                      {subtitle}
                    </Hidden>
                  )}
                </SpacerWrapper>
              )}
              <PremiumReviewToggle type="alternativePage" showBackground={false} />
              {/* @ts-expect-error - Automatic, Please fix when editing this file */}
              <ProductTiles
                {...{ customer, isLoadingPrice, prices, products: productsToLoad, useFutureEffectiveDate }}
                withHorizontalScroll={withHorizontalScroll}
              />
              {compareButtonToLink && compareButtonText && (
                <Button
                  // @ts-expect-error - Automatic, Please fix when editing this file
                  className={classNames({ [styles.withNeedsLink]: withNeedsLink })}
                  to={compareButtonToLink}
                  variant={[styles.compareButton, 'border', 'white']}
                >
                  {compareButtonText}
                </Button>
              )}
              {withNeedsLink && (
                <StyledNeedsCTA variant="marginBottomSpacer8">
                  Not sure what you need?{' '}
                  <Clickable to={constants.URLS.SALES.REFINE_PRICE}>
                    <Strong>Tailor your health cover</Strong>
                  </Clickable>
                </StyledNeedsCTA>
              )}
              <div>
                <PriceDisclaimer bold showRefineLink highlightIncome forProducts={productsToLoad} center />
              </div>
            </div>
          </div>
        </TypeWrapper>
        {withDrawer && <PricingDrawer isProductWithHospitalServices showIncomeOptions />}
      </WrappedSection>
    </>
  );
};

// @ts-expect-error - Automatic, Please fix when editing this file
const mapStateToProps = (state: RootState, props) => ({
  customer: selectCustomer(state),
  isLoadingPrice: state.price.loading,
  needsProductType: selectNeedsProductType(state, props),
  needsType: selectNeedsType(state, props),
  prices: state.price.data,
  stateProducts: state.product.data,
  useFutureEffectiveDate: state.price.useFutureEffectiveDate,
});

export default compose(
  NeedsProductDataLoader,
  connect(mapStateToProps, productListingCommon.mapDispatchToProps),
)(ProductsSection) as ComponentType<Omit<ProductsSectionProps, keyof ReturnType<typeof mapStateToProps>>>;

export { ProductsSection as ProductsSectionForTest };
